import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Hero } from "../components/Hero";
import { PromoStrip } from "../components/PromoStrip";
import { ColorLab } from "../components/ColorLab";
import { BrandColors } from "../components/BrandColors";
import { WorkGallery } from "../components/WorkGallery";
import { PaintCalculator } from "../components/PaintCalculator";
import { ProductCatalog } from "../components/ProductCatalog";
import { LocationContact } from "../components/LocationContact";
import { Footer } from "../components/Footer";
import { CartDrawer } from "../components/CartDrawer";
import { api } from "../lib/api";
import { useFavorites } from "../context/FavoritesContext";

const SECTION_IDS = {
  lab: "section-lab", colores: "section-colors", galeria: "section-gallery",
  promos: "section-promos", calc: "section-calc", contacto: "section-contacto",
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Storefront() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState(params.get("p") || "");
  const [activeSector, setActiveSector] = useState("");
  const [labHex, setLabHex] = useState("#0067B1");
  const [onlyFavs, setOnlyFavs] = useState(false);
  const { codes: favCodes } = useFavorites();

  useEffect(() => {
    Promise.all([api.listProducts(), api.stats()])
      .then(([p, s]) => { setAllProducts(p); setStats(s); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    if (params.get("p")) {
      setTimeout(() => scrollTo("section-catalog"), 150);
      setParams({}, { replace: true });
    }
    if (location.state?.nav) {
      const { nav, value } = location.state;
      setTimeout(() => handleNav(nav, value), 150);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const products = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allProducts.filter((p) => {
      const matchSector = !activeSector || p.sector === activeSector;
      const matchSearch = !q || [p.name, p.code, p.brand, p.description].join(" ").toLowerCase().includes(q);
      const matchFav = !onlyFavs || favCodes.includes(p.code);
      return matchSector && matchSearch && matchFav;
    });
  }, [allProducts, search, activeSector, onlyFavs, favCodes]);

  const handleNav = (key, value) => {
    if (SECTION_IDS[key]) return scrollTo(SECTION_IDS[key]);
    if (key === "search") { setActiveSector(""); setOnlyFavs(false); setSearch(value || ""); return scrollTo("section-catalog"); }
    if (key === "favoritos") { setActiveSector(""); setSearch(""); setOnlyFavs(true); return scrollTo("section-catalog"); }
    setActiveSector(key === "all" ? "" : key);
    scrollTo("section-catalog");
  };

  const pickColor = (hex) => { setLabHex(hex); scrollTo("section-lab"); };

  return (
    <div className="App">
      <Header search={search} setSearch={setSearch} onNav={handleNav} />
      <Hero stats={stats} onNav={handleNav} />
      <PromoStrip onNav={handleNav} />
      <ProductCatalog
        products={products} loading={loading}
        activeSector={activeSector} setActiveSector={setActiveSector} search={search}
        onlyFavs={onlyFavs} setOnlyFavs={setOnlyFavs}
      />
      <PaintCalculator products={allProducts} />
      <BrandColors onPick={pickColor} />
      <ColorLab hex={labHex} setHex={setLabHex} />
      <WorkGallery />
      <LocationContact />
      <Footer />
      <CartDrawer />
    </div>
  );
}
